import { Page, TextField, Modal } from '../components/forms';
import RouteRoadPlanning from '../components/routes/RouteRoadPlanning';
import RouteManifestPreview from '../components/routes/RouteManifestPreview';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  DataTable,
  Spinner,
  DescriptionList,
  Divider,
  ProgressBar,
  Icon,
} from '@shopify/polaris';
import { PlusIcon,
  LocationIcon,
} from '@shopify/polaris-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { useRouteSave } from '../components/routes/useRouteSave';
import RouteFleet from '../components/fleet/RouteFleet';
import RouteLoading from '../components/fleet/RouteLoading';
import RouteStopPlanning from '../components/routes/RouteStopPlanning';

interface DeliveryRoute {
  id: number;
  revision: number;
  name: string | null;
  delivery_date: string;
  zone_name: string | null;
  driver_name: string | null;
  vehicle: string | null;
  status: string;
  total_distance_km: number | null;
  total_duration_minutes: number | null;
  estimated_start_time: string | null;
  notes: string | null;
}

interface RouteStop {
  id: number;
  order_id: number;
  shipment_id: string | null;
  tracking_token: string | null;
  order_number: string;
  customer_name: string;
  address_line1: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  estimated_arrival: string | null;
  status: string;
  failure_reason: string | null;
}

interface RouteHistory {
  id: number;
  action: string;
  performed_by_name: string;
  created_at: string;
}

export default function RouteDetail() { const { user,currentShop,currentOrganization } = useAuth(); const { routeId } = useParams(); return <Detail key={`${user?.id}:${currentOrganization?.id}:${currentShop?.id}:${routeId}`} />; }
function Detail() {
  const [manifestOpen, setManifestOpen] = useState(false);
  const navigate = useNavigate();
  const { routeId } = useParams<{ routeId: string }>();
  const { fetchApi, error, loading } = useApi(); const { hasPermission } = useAuth();
  const [route, setRoute] = useState<DeliveryRoute | null>(null);
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [history, setHistory] = useState<RouteHistory[]>([]);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRevision, setEditRevision] = useState(0);
  const [editDate, setEditDate] = useState('');

  const [editStartTime, setEditStartTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const mutation = useRouteSave(); const saving = mutation.saving;
  const generation = useRef(0);

  // Stop status modal
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [selectedStop, setSelectedStop] = useState<RouteStop | null>(null);
  const [stopStatus, setStopStatus] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [recipientName, setRecipientName] = useState('');

  // Confirm modals
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [optimizeModalOpen, setOptimizeModalOpen] = useState(false);
  const optimization = useRouteSave(); const optimizing = optimization.saving;

  const loadRoute = useCallback(async () => {
    const version = ++generation.current;
    const data = await fetchApi<{
      route: DeliveryRoute;
      stops: RouteStop[];
      history: RouteHistory[];
    }>(`/routes/${routeId}`);

    if (version !== generation.current) return;
    if (!data) { setRoute(null); setStops([]); setHistory([]); return; }
    if (data) {
      setRoute(data.route);
      setStops(data.stops || []);
      setHistory(data.history || []);
    }
  }, [fetchApi, routeId]);

  useEffect(() => {
    loadRoute(); return () => { generation.current++; };
  }, [loadRoute]);

  const handleUpdate = async () => {
    if (!route) return;
    const result = await mutation.save(`/routes/${routeId}`, 'PUT', {
      revision: editRevision, name: editName.trim() || null, delivery_date: editDate,
      estimated_start_time: editStartTime || null, notes: editNotes.trim() || null,
    });
    if (result?.success) { setEditModalOpen(false); loadRoute(); }
  };

  const handleStart = async () => {
    const result = await fetchApi<{ success: boolean }>(`/routes/${routeId}/start`, { method: 'POST' });
    if (result?.success) { setStartModalOpen(false); loadRoute(); }
  };

  const handleCancel = async () => {
    const result = await fetchApi<{ success: boolean }>(`/routes/${routeId}/cancel?revision=${route?.revision}`, { method: 'POST' });
    if (result?.success) { setCancelModalOpen(false); loadRoute(); }
  };

  const handleOptimize = async () => {
    const result = await optimization.save(`/routes/${routeId}/optimize`, 'POST', { revision: route?.revision });
    if (result?.success) { setOptimizeModalOpen(false); loadRoute(); }
  };

  const handleStopStatus = async () => {
    if (!selectedStop || !stopStatus) return;

    const result = await fetchApi<{ success: boolean }>(`/routes/${routeId}/stops/${selectedStop.id}/status`, {
      method: 'POST',
      body: JSON.stringify({
        status: stopStatus,
        failure_reason: stopStatus === 'failed' ? failureReason : undefined,
        recipient_name: stopStatus === 'completed' ? recipientName : undefined,
      }),
    });
    if (!result?.success) return;
    setStopModalOpen(false);
    setSelectedStop(null);
    setStopStatus('');
    setFailureReason('');
    setRecipientName('');
    loadRoute();
  };

  const handleRemoveStop = async (stopId: number) => {
    const result = await fetchApi<{ success: boolean }>(`/routes/${routeId}/stops/${stopId}?revision=${route?.revision}`, { method: 'DELETE' });
    if (result?.success) loadRoute();
  };

  const openEditModal = () => {
    if (mutation.locked) { setEditModalOpen(true); return; }
    if (route) {
      setEditRevision(route.revision);
      setEditName(route.name || '');
      setEditDate(route.delivery_date);
      setEditStartTime(route.estimated_start_time || '');
      setEditNotes(route.notes || '');
      setEditModalOpen(true);
    }
  };

  const openStopModal = (stop: RouteStop, newStatus: string) => {
    setSelectedStop(stop);
    setStopStatus(newStatus);
    setStopModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'info' | 'success' | 'warning' | 'critical' | 'attention'> = {
      draft: 'attention',
      planned: 'info',
      in_progress: 'warning',
      completed: 'success',
      cancelled: 'critical',
    };
    return <Badge tone={tones[status] || 'info'}>{status.replace('_', ' ')}</Badge>;
  };

  const getStopStatusBadge = (status: string) => {
    const config: Record<string, { tone: 'info' | 'success' | 'warning' | 'critical' | 'attention'; label: string }> = {
      pending: { tone: 'attention', label: 'Pending' },
      en_route: { tone: 'warning', label: 'En Route' },
      arrived: { tone: 'info', label: 'Arrived' },
      completed: { tone: 'success', label: 'Delivered' },
      partial: { tone: 'warning', label: 'Partly received' },
      failed: { tone: 'critical', label: 'Failed' },
      skipped: { tone: 'critical', label: 'Skipped' },
      transferred: { tone: 'info', label: 'Transferred to another van' },
    };
    const c = config[status] || { tone: 'info', label: status };
    return <Badge tone={c.tone}>{c.label}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(`${dateStr.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    if (timeStr.includes('T')) {
      return new Date(timeStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return timeStr.slice(0, 5);
  };

  if (loading && !route) {
    return (
      <Page title="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!route) {
    return (
      <Page title="Route unavailable">
        <Banner tone="critical">{error || 'Route not found'}</Banner>
      </Page>
    );
  }

  const completedStops = stops.filter(s => ['completed','partial','failed','skipped','transferred'].includes(s.status)).length;
  const progress = stops.length > 0 ? Math.round((completedStops / stops.length) * 100) : 0;
  const canStart = hasPermission('routes.operate') && ['draft', 'planned'].includes(route.status);
  const canEdit = hasPermission('routes.update') && ['draft', 'planned'].includes(route.status);
  const isActive = route.status === 'in_progress';

  const stopRows = stops.map((stop, index) => {
    const isCurrent = route.status === 'in_progress' && stop.status === 'en_route';
    return [
      <InlineStack gap="200" align="center">
        <Text as="span" variant="bodyMd" fontWeight={isCurrent ? 'bold' : 'regular'}>
          {index + 1}
        </Text>
        {isCurrent && <Icon source={LocationIcon} tone="success" />}
      </InlineStack>,
      <BlockStack gap="100">
        <Button variant="plain" onClick={() => navigate(`/orders/${stop.order_id}`)}>
          {stop.order_number}
        </Button>
        <Text as="span" variant="bodySm" fontWeight="semibold">{stop.customer_name}</Text>
      </BlockStack>,
      <BlockStack gap="050">
        <Text as="span" variant="bodySm">{stop.address_line1}</Text>
        <Text as="span" variant="bodySm" tone="subdued">{stop.postcode}</Text>
      </BlockStack>,
      formatTime(stop.estimated_arrival),
      getStopStatusBadge(stop.status),
      <InlineStack gap="200">
          {canEdit && <RouteStopPlanning key={`${stop.id}-${route.revision}`} routeId={routeId!} revision={route.revision} stops={stops} stop={stop} index={index} onSaved={loadRoute} />}
        {isActive && stop.status === 'en_route' && (
          <>
            <Button size="slim" onClick={() => openStopModal(stop, 'arrived')}>Arrived</Button>
          </>
        )}
        {isActive && stop.status === 'arrived' && !stop.shipment_id && (
          <>
            <Button size="slim" variant="primary" onClick={() => openStopModal(stop, 'completed')}>Complete</Button>
            <Button size="slim" tone="critical" onClick={() => openStopModal(stop, 'failed')}>Failed</Button>
          </>
        )}
        {stop.shipment_id && <Button size="slim" url={`/orders/${stop.order_id}/shipments`}>Shipment handover</Button>}
        {stop.tracking_token && stop.tracking_token.length >= 16 && <Button size="slim" url={`${window.location.origin}/track/${encodeURIComponent(stop.tracking_token)}`} external>Customer tracking</Button>}
        {canEdit && (
          <Button size="slim" tone="critical" onClick={() => handleRemoveStop(stop.id)}>Remove</Button>
        )}
      </InlineStack>,
    ];
  });

  return (
    <Page
      title={route.name || `Route ${route.id}`}
      subtitle={`${formatDate(route.delivery_date)} - ${route.zone_name || 'All Zones'}`}
      backAction={{ content: 'Routes', onAction: () => navigate('/routes') }}
      titleMetadata={getStatusBadge(route.status)}
      primaryAction={
        canStart && stops.length > 0
          ? { content: 'Start Route', onAction: () => setStartModalOpen(true) }
          : isActive
          ? { content: 'Track Route', disabled: true }
          : undefined
      }
      secondaryActions={[
        ...(hasPermission('orders.view') && hasPermission('routes.view') ? [{ content: 'Print route manifest', onAction: () => setManifestOpen(true) }] : []),
        ...(canEdit ? [{ content: 'Edit Route', onAction: openEditModal }] : []),
        ...(canEdit && hasPermission('routes.optimize') && stops.length > 1 ? [{ content: 'Order by distance', onAction: () => setOptimizeModalOpen(true) }] : []),
        ...(canEdit ? [{ icon: PlusIcon, content: 'Add Orders', onAction: () => navigate(`/routes/build?route=${routeId}`) }] : []),
        ...(canEdit
          ? [{ content: 'Cancel', destructive: true, onAction: () => setCancelModalOpen(true) }]
          : []),
      ]}
    >
      {manifestOpen && hasPermission('orders.view') && hasPermission('routes.view') &&
        <RouteManifestPreview routeId={routeId!} onClose={() => setManifestOpen(false)} />}
      <Layout>
        {(error || mutation.error) && (
          <Layout.Section>
            <Banner tone="critical">{error || mutation.error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section><RouteFleet key={route.revision} routeId={routeId!} routeStatus={route.status} onSaved={loadRoute} /></Layout.Section>
        <Layout.Section><RouteRoadPlanning routeId={routeId!} onSaved={loadRoute} /></Layout.Section>
        <Layout.Section><RouteLoading key={route.revision} routeId={routeId!} routeStatus={route.status} /></Layout.Section>

        {/* Route Summary */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Route Details</Text>

              {/* Progress */}
              {(isActive || route.status === 'completed') && (
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodySm">Progress</Text>
                    <Text as="span" variant="bodySm">{completedStops}/{stops.length} stops closed</Text>
                  </InlineStack>
                  <ProgressBar progress={progress} tone={progress === 100 ? 'success' : 'primary'} />
                </BlockStack>
              )}

              <DescriptionList
                items={[
                  { term: 'Date', description: formatDate(route.delivery_date) },
                  { term: 'Zone', description: route.zone_name || 'All Zones' },
                  { term: 'Driver', description: route.driver_name || '-' },
                  { term: 'Vehicle', description: route.vehicle || '-' },
                  { term: 'Start Time', description: formatTime(route.estimated_start_time) },
                  { term: 'Total Stops', description: String(stops.length) },
                  { term: 'Straight-line estimate', description: route.total_distance_km !== null ? `${route.total_distance_km} km` : '-' },
                  { term: 'Est. Duration', description: route.total_duration_minutes ? `${route.total_duration_minutes} min` : '-' },
                ]}
              />

              <Text as="p" tone="subdued">Each stop has its own customer tracking link. Add split deliveries from the Shipments screen.</Text>

              {route.notes && (
                <>
                  <Divider />
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">Notes</Text>
                    <Text as="p" variant="bodySm">{route.notes}</Text>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Stops */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Stops ({stops.length})</Text>
                {canEdit && (
                  <InlineStack gap="200"><Button icon={PlusIcon} url="/shipments">Add shipments</Button><Button icon={PlusIcon} onClick={() => navigate(`/routes/build?route=${routeId}`)}>Add whole orders</Button></InlineStack>
                )}
              </InlineStack>

              {stops.length === 0 ? (
                <Banner tone="info">
                  No stops on this route. Add orders to create delivery stops.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text']}
                  headings={['#', 'Order', 'Address', 'ETA', 'Status', 'Actions']}
                  rows={stopRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* History */}
        {history.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">History</Text>
                {history.slice(0, 10).map((h) => (
                  <InlineStack key={h.id} gap="400" align="start">
                    <div style={{ width: '120px' }}>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {new Date(h.created_at).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    </div>
                    <BlockStack gap="050">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {h.action.replace(/_/g, ' ')}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {h.performed_by_name}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Route"
        primaryAction={{
          content: 'Save',
          onAction: handleUpdate,
          loading: saving,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {mutation.error && <Banner tone="critical">{mutation.error}</Banner>}
            {mutation.locked && !saving && <Button onClick={() => { mutation.reset(); setEditModalOpen(false); loadRoute(); }}>Discard form and reload saved route</Button>}
            <TextField
              disabled={mutation.locked}
              label="Route Name"
              requiredIndicator value={editName}
              onChange={setEditName}
              autoComplete="off"
            />
            <TextField label="Delivery date" type="date" requiredIndicator value={editDate} onChange={setEditDate} autoComplete="off" disabled={mutation.locked} />
            <Text as="p">Choose the van and driver in the fleet booking panel. Release that booking before changing this date or start time.</Text>
            <TextField
              disabled={mutation.locked}
              label="Start Time"
              type="time"
              value={editStartTime}
              onChange={setEditStartTime}
              autoComplete="off"
            />
            <TextField
              disabled={mutation.locked}
              label="Notes"
              value={editNotes}
              onChange={setEditNotes}
              autoComplete="off"
              multiline={3}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Stop Status Modal */}
      <Modal
        open={stopModalOpen}
        onClose={() => {
          setStopModalOpen(false);
          setSelectedStop(null);
        }}
        title={`Update Stop - ${selectedStop?.order_number}`}
        primaryAction={{
          content: stopStatus === 'completed' ? 'Mark Delivered' : stopStatus === 'failed' ? 'Mark Failed' : 'Update',
          onAction: handleStopStatus,
          destructive: stopStatus === 'failed',
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setStopModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {error && <Banner tone="critical">{error || mutation.error}</Banner>}
            {selectedStop && (
              <DescriptionList
                items={[
                  { term: 'Customer', description: selectedStop.customer_name },
                  { term: 'Address', description: `${selectedStop.address_line1}, ${selectedStop.postcode}` },
                ]}
              />
            )}

            {stopStatus === 'completed' && (
              <TextField
                label="Recipient Name"
                value={recipientName}
                onChange={setRecipientName}
                autoComplete="off"
                placeholder="Who received the delivery?"
              />
            )}

            {stopStatus === 'failed' && (
              <TextField
                label="Failure Reason"
                requiredIndicator value={failureReason}
                onChange={setFailureReason}
                autoComplete="off"
                placeholder="Why couldn't the delivery be completed?"

              />
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Start Route Modal */}
      <Modal
        open={startModalOpen}
        onClose={() => setStartModalOpen(false)}
        title="Start Route"
        primaryAction={{
          content: 'Start Route',
          onAction: handleStart,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setStartModalOpen(false) }]}
      >
        <Modal.Section>
          {(error || mutation.error) && <Banner tone="critical">{error || mutation.error}</Banner>}
          <Text as="p">
            Are you ready to start this route? This will begin tracking and allow stop updates.
          </Text>
          <Text as="p" tone="subdued">
            {stops.length} stops to deliver
          </Text>
        </Modal.Section>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Route"
        primaryAction={{
          content: 'Cancel Route',
          onAction: handleCancel,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Keep Route', onAction: () => setCancelModalOpen(false) }]}
      >
        <Modal.Section>
          {(error || mutation.error) && <Banner tone="critical">{error || mutation.error}</Banner>}
          <Text as="p">
            Are you sure you want to cancel this route?
          </Text>
          <Text as="p" tone="subdued">
            All orders will be unassigned from this route.
          </Text>
        </Modal.Section>
      </Modal>

      {/* Optimize Modal */}
      <Modal
        open={optimizeModalOpen}
        onClose={() => setOptimizeModalOpen(false)}
        title="Order stops by straight-line distance"
        primaryAction={{
          content: 'Order by distance',
          onAction: handleOptimize,
          loading: optimizing,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setOptimizeModalOpen(false) }]}
      >
        <Modal.Section>
          {(error || optimization.error) && <Banner tone="critical">{error || optimization.error}</Banner>}
          {optimization.locked && !optimizing && <Button onClick={() => { optimization.reset(); setOptimizeModalOpen(false); loadRoute(); }}>Reload saved route</Button>}
          <Text as="p">
            This keeps the first stop and chooses each next stop by straight-line distance. It does not account for roads, traffic or delivery time windows.
          </Text>
          <Text as="p" tone="subdued">
            Every stop needs valid coordinates. The distance excludes travel from and back to your warehouse.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
